import type { FC } from 'react';
import { lazy } from 'react';
import { useMemoOne } from 'use-memo-one';
import type {
  NwpCapacityGraphParams,
  ShipperTransactionsParams,
} from 'src/apis/capacity-management/types';
import type { CapacityManagementRootRequest } from 'src/apis/capacity-management/use-capacity-management-root';
import { useNwpCapacityGraph } from 'src/apis/capacity-management/use-nwp-capacity-graph';
import { useShipperTransactions } from 'src/apis/capacity-management/use-shipper-transactions';
import type { NetworkPoint } from 'src/apis/monolith/types';
import type { NetworkPointRequest } from 'src/apis/monolith/use-network-point';
import { useSurrenderRoot } from 'src/apis/surrender-service/use-surrender-root';
import { PageSpinner } from 'src/components/spinner-container';
import type { Successful } from 'src/hooks/use-axios';
import { useCapacityOverviewPageParams } from 'src/pages/transport/network-points/details/capacity-overview/use-page-params';

const Page = lazy(() => import('./page'));

type Props = {
  networkPoint: Successful<NetworkPointRequest>;
  capacityManagementRoot: Successful<CapacityManagementRootRequest>;
};

export const CapacityOverview: FC<Props> = ({
  networkPoint,
  capacityManagementRoot,
}) => {
  return (
    <Content
      networkPoint={networkPoint.response.data}
      capacityManagementRoot={capacityManagementRoot}
    />
  );
};

type ContentProps = {
  networkPoint: NetworkPoint;
  capacityManagementRoot: Successful<CapacityManagementRootRequest>;
};

const Content: FC<ContentProps> = ({
  networkPoint,
  capacityManagementRoot,
}) => {
  const pageParams = useCapacityOverviewPageParams();

  const transactionsParams = useMemoOne<ShipperTransactionsParams>(() => {
    const {
      start,
      pageSize,
      sortBy,
      sortDirection,
      runtimeIntersectionFilterStart,
      runtimeIntersectionFilterEnd,
    } = pageParams.value;

    return {
      offset: start,
      limit: pageSize,
      sortBy,
      sortDirection,
      status: ['SUCCESSFUL'],
      networkPointId: [networkPoint.networkPointUuid],
      runtimeIntersectionFilterStart,
      runtimeIntersectionFilterEnd,
    };
  }, [pageParams.value, networkPoint.networkPointUuid]);

  const shipperTransactions = useShipperTransactions({
    capacityManagementRoot,
    params: transactionsParams,
    throwOnForbidden: false,
  });

  const paramsForGraph = useMemoOne<NwpCapacityGraphParams>(() => {
    return {
      runtimeIntersectionFilterStart:
        pageParams.value.runtimeIntersectionFilterStart,
      runtimeIntersectionFilterEnd:
        pageParams.value.runtimeIntersectionFilterEnd,
      granularity: pageParams.value.granularity,
      nwpId: networkPoint.networkPointUuid,
    };
  }, [pageParams.value, networkPoint.networkPointUuid]);

  const nwpCapacityGraph = useNwpCapacityGraph({
    capacityManagementRoot,
    params: paramsForGraph,
  });

  const surrenderRoot = useSurrenderRoot();

  if (!surrenderRoot.response) return <PageSpinner />;

  return (
    <Page
      networkPoint={networkPoint}
      shipperTransactions={shipperTransactions}
      pageParams={pageParams}
      nwpCapacityGraph={nwpCapacityGraph}
      isSurrenderForAllEnabled={
        !!surrenderRoot.response.data._links.isSurrenderForAllEnabled
      }
    />
  );
};
